import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Zoom,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { CheckCircle, Close } from '@mui/icons-material';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { ActivateCardFormContent, ActivateCardFormHandle } from './ActivateCardAction';
import SetPasswordAction from './SetPasswordAction';
import { ConfirmationModal } from '../../../../../components/ConfirmationModal';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>
) {
    return <Slide direction="down" ref={ref} {...props} />;
});

type CardFlowMode = 'add' | 'activate';

export interface AddOrActivateCardModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const AddOrActivateCardModal: React.FC<AddOrActivateCardModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [mode, setMode] = useState<CardFlowMode>('activate');
    const formRef = useRef<ActivateCardFormHandle>(null);
    const [formLoading, setFormLoading] = useState(false);
    const [isSetPasswordModalOpen, setIsSetPasswordModalOpen] = useState(false);
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setMode('activate');
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setShowSubmitConfirm(false);
        }
    }, [isOpen]);

    const handleActivateSucceeded = useCallback(() => {
        setIsSetPasswordModalOpen(true);
    }, []);

    const handleDialogClose = useCallback(() => {
        if (formRef.current) {
            formRef.current.reset();
        } else {
            onClose();
        }
    }, [onClose]);

    return (
        <>
            <Dialog
                TransitionComponent={Transition}
                open={isOpen}
                onClose={undefined}
                fullScreen={false}
                disableEscapeKeyDown
                aria-labelledby="add-or-activate-card-title"
                PaperProps={{
                    sx: {
                        width: '100%',
                        maxWidth: isMobile ? '100%' : '500px',
                        maxHeight: '90vh',
                        zIndex: 99999999,
                        backgroundColor: theme.palette.grey[50],
                        borderRadius: isMobile ? 1 : undefined,
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                        padding: { xs: '12px 16px', sm: '16px 20px' },
                        borderRadius: '4px 4px 0 0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(255,255,255,0.1)',
                            backdropFilter: 'blur(10px)',
                            zIndex: 0,
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
                        <Avatar
                            sx={{
                                background: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                width: 32,
                                height: 32,
                            }}
                        >
                            <CheckCircle fontSize="small" />
                        </Avatar>
                        <Typography
                            id="add-or-activate-card-title"
                            variant="h6"
                            sx={{
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '1rem !important',
                                textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                            }}
                        >
                            Kart Ekle / Aktive Et
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleDialogClose}
                        sx={{
                            color: 'white',
                            position: 'relative',
                            zIndex: 1,
                            width: 36,
                            height: 36,
                            border: '1px solid',
                            borderColor: 'rgba(255,255,255,0.3)',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                borderColor: 'rgba(255,255,255,0.5)',
                                transform: 'rotate(90deg)',
                            },
                            transition: 'all 0.2s ease-in-out',
                        }}
                    >
                        <Close sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>

                <Zoom in timeout={500}>
                    <DialogContent sx={{ m: 0, px: { xs: 2, sm: 3 }, py: 2, '&.MuiDialogContent-root': { m: 0 } }}>
                        <ToggleButtonGroup
                            value={mode}
                            exclusive
                            onChange={(_, value: CardFlowMode | null) => {
                                if (value !== null) {
                                    setMode(value);
                                }
                            }}
                            aria-label="Kart ekle veya aktive et"
                            sx={{
                                width: '100%',
                                mb: 2,
                                p: 0.5,
                                bgcolor: 'rgba(30, 60, 114, 0.08)',
                                borderRadius: 2,
                                border: 'none',
                                gap: 0.5,
                                '& .MuiToggleButtonGroup-grouped': {
                                    border: 0,
                                    mx: 0,
                                    borderRadius: '8px !important',
                                },
                            }}
                        >
                            <ToggleButton
                                value="add"
                                sx={{
                                    flex: 1,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    py: 1,
                                    '&.Mui-selected': {
                                        color: 'white',
                                        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                        boxShadow: '0 2px 10px rgba(30, 60, 114, 0.45)',
                                        '&:hover': {
                                            background: 'linear-gradient(0deg, rgba(0,0,0,0.12), rgba(0,0,0,0.12)), linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                            color: 'white',
                                        },
                                    },
                                }}
                            >
                                Kart Ekle
                            </ToggleButton>
                            <ToggleButton
                                value="activate"
                                sx={{
                                    flex: 1,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    py: 1,
                                    '&.Mui-selected': {
                                        color: 'white',
                                        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                        boxShadow: '0 2px 10px rgba(30, 60, 114, 0.45)',
                                        '&:hover': {
                                            background: 'linear-gradient(0deg, rgba(0,0,0,0.12), rgba(0,0,0,0.12)), linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                            color: 'white',
                                        },
                                    },
                                }}
                            >
                                Kart Aktive Et
                            </ToggleButton>
                        </ToggleButtonGroup>

                        {mode === 'add' ? (
                            <ActivateCardFormContent
                                key="card-flow-add"
                                ref={formRef}
                                flowMode="add"
                                isActive={isOpen && mode === 'add'}
                                onClose={onClose}
                                onLoadingChange={setFormLoading}
                                onAddCardSuccess={() => onSuccess?.()}
                            />
                        ) : (
                            <ActivateCardFormContent
                                key="card-flow-activate"
                                ref={formRef}
                                flowMode="activate"
                                isActive={isOpen && mode === 'activate'}
                                onClose={onClose}
                                onLoadingChange={setFormLoading}
                                onActivateSucceeded={handleActivateSucceeded}
                            />
                        )}
                    </DialogContent>
                </Zoom>

                <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1 }}>
                    <Button
                        onClick={handleDialogClose}
                        sx={{
                            color: '#0b6bcb !important',
                            border: '1px solid #0b6bcb',
                            fontSize: '0.875rem',
                            height: '30px',
                            padding: '4px 16px',
                        }}
                    >
                        Vazgeç
                    </Button>
                    {(mode === 'activate' || mode === 'add') && (
                        <Button
                            disabled={formLoading}
                            onClick={() => setShowSubmitConfirm(true)}
                            sx={{
                                color: 'white !important',
                                height: '30px',
                                minWidth: '120px',
                                padding: '4px 16px !important',
                                fontSize: '0.875rem',
                                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(0deg, rgba(0,0,0,0.15), rgba(0,0,0,0.15)), linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                    color: 'white',
                                },
                                '&.Mui-disabled': {
                                    background: '#1e3c7280',
                                    color: 'white !important',
                                },
                            }}
                        >
                            {!formLoading ? (
                                mode === 'add' ? 'Ekle' : 'Kaydet'
                            ) : (
                                <CircularProgress sx={{ width: '20px !important', height: '20px !important', color: 'white' }} />
                            )}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <SetPasswordAction
                isOpen={isSetPasswordModalOpen}
                onClose={() => setIsSetPasswordModalOpen(false)}
                onSuccess={() => {
                    setIsSetPasswordModalOpen(false);
                    onSuccess?.();
                }}
            />

            <ConfirmationModal
                isOpen={showSubmitConfirm}
                onClose={() => setShowSubmitConfirm(false)}
                onConfirm={() => {
                    setShowSubmitConfirm(false);
                    formRef.current?.submit();
                }}
                title="İşlem Onayı"
                content={
                    mode === 'add'
                        ? 'Kartı eklemek istediğinize emin misiniz?'
                        : 'Kartı aktive etmek istediğinize emin misiniz?'
                }
                confirmButtonText="Onayla"
                closeButtonText="İptal"
                isLoading={false}
                optionalWidth="440px"
            />
        </>
    );
};

export default AddOrActivateCardModal;
